import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { API_URL } from '../config';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user, token: authToken } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  // Review-queue presence: { [ticketId]: [{ userId, userName, userEmail, since }] }.
  // The server owns the authoritative map and broadcasts the FULL state on every
  // change, so this is always a wholesale replace — never a merge.
  const [reviewViewers, setReviewViewers] = useState({});
  const socketRef = useRef(null);
  // Desired review-presence state, mirrored in refs so the 'authenticated'
  // handler can replay it after a reconnect (see the comment there).
  const reviewQueueEnteredRef = useRef(false);
  const reviewViewingTicketIdRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const visibilityHandlerRef = useRef(null);

  // Initialize socket connection
  useEffect(() => {
    if (!user) {
      // Clean up socket if user logs out
      if (socketRef.current) {
        console.log('🔌 Disconnecting socket (user logged out)');
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }

      // Clear intervals
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      return;
    }

    // Only create socket once when user first logs in
    if (socketRef.current) {
      console.log('🔌 Socket already exists, skipping creation');
      return;
    }

    console.log('🔌 Creating new socket connection for user:', user.email);

    // Create socket connection with improved settings for background tabs
    const newSocket = io(API_URL, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity, // Never stop trying to reconnect
      timeout: 20000,
      // Prevent socket from being throttled in background tabs
      transports: ['websocket', 'polling'], // Prefer WebSocket
      upgrade: true,
      // Keep connection alive with ping/pong
      pingInterval: 25000, // Send ping every 25 seconds
      pingTimeout: 60000, // Wait 60 seconds for pong before disconnect
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Connection event handlers
    newSocket.on('connect', () => {
      console.log('🔌 Socket connected (ID:', newSocket.id, ')');
      setIsConnected(true);

      // Authenticate socket
      const token = localStorage.getItem('token');
      if (token) {
        console.log('🔐 Sending authentication...');
        newSocket.emit('authenticate', { token });
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected - Reason:', reason);
      setIsConnected(false);
      setWorkspaceUsers([]);
      // Presence is only meaningful while connected — keep no stale viewers on screen
      setReviewViewers({});

      // If disconnected due to transport error or ping timeout, try to reconnect immediately
      if (reason === 'transport error' || reason === 'ping timeout') {
        console.log('🔄 Attempting immediate reconnection...');
        newSocket.connect();
      }
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Socket reconnected after', attemptNumber, 'attempts');

      // Re-authenticate after reconnection
      const token = localStorage.getItem('token');
      if (token) {
        console.log('🔐 Re-authenticating after reconnect...');
        newSocket.emit('authenticate', { token });
      }
    });

    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Socket reconnection attempt:', attemptNumber);
    });

    newSocket.on('reconnect_error', (error) => {
      console.error('❌ Socket reconnection error:', error);
    });

    newSocket.on('reconnect_failed', () => {
      console.error('❌ Socket reconnection failed after all attempts');
      // Just try to reconnect — AuthContext handles token refresh
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        console.log('🔄 Retrying connection with current token...');
        newSocket.connect();
      }
    });

    newSocket.on('authenticated', (data) => {
      console.log('🔐 Socket authenticated:', data);

      // IMPORTANT: Re-initialize chat on every authentication (including reconnections)
      // This ensures user rejoins all chat channels after reconnection
      console.log('🔄 Emitting chat:init after authentication...');
      newSocket.emit('chat:init');

      // Same reasoning for review-queue presence, but the stakes are higher: the
      // server keeps presence in an in-memory Map, so a backend deploy (a ~4s pm2
      // restart) empties it while this tab stays open. Replaying enter + whatever
      // ticket this tab is on makes presence heal itself; without it, presence
      // silently dies until the user happens to reload.
      if (reviewQueueEnteredRef.current) {
        console.log('🔄 Re-entering review presence queue after authentication...');
        newSocket.emit('review:presence:enter', {});
        if (reviewViewingTicketIdRef.current) {
          newSocket.emit('review:presence:viewing', { ticketId: reviewViewingTicketIdRef.current });
        }
      }
    });

    newSocket.on('auth_error', (error) => {
      console.error('❌ Socket auth error:', error);
      // Token refresh is handled by AuthContext's proactive refresh.
      // When AuthContext gets a new token, the token-change useEffect
      // will re-authenticate this socket automatically.
      console.log('⏳ Waiting for AuthContext to refresh token...');
    });

    newSocket.on('error', (error) => {
      console.error('❌ Socket error:', error);
    });

    // Workspace event handlers
    newSocket.on('workspace:users:list', (data) => {
      setWorkspaceUsers(data.users || []);
    });

    newSocket.on('workspace:user:joined', (data) => {
      setWorkspaceUsers(prev => [...prev.filter(u => u.userId !== data.userId), {
        userId: data.userId,
        userName: data.userName,
        userEmail: data.userEmail,
        cursor: null,
        joinedAt: data.timestamp
      }]);
    });

    newSocket.on('workspace:user:left', (data) => {
      setWorkspaceUsers(prev => prev.filter(u => u.userId !== data.userId));
    });

    // Review-queue presence — arrives as the complete state (unicast right after
    // 'review:presence:enter', then broadcast on every change), so assign it as-is
    newSocket.on('review:presence:state', (data) => {
      setReviewViewers(data?.viewers || {});
    });

    // Set up custom heartbeat to keep connection alive in background tabs
    // Browsers throttle setInterval in background tabs, but we use a longer interval
    heartbeatIntervalRef.current = setInterval(() => {
      if (newSocket.connected) {
        // Send a lightweight ping to keep connection alive
        newSocket.emit('heartbeat', { timestamp: Date.now() });
      } else {
        // Try to reconnect if disconnected
        console.log('💓 Heartbeat detected disconnect, reconnecting...');
        newSocket.connect();
      }
    }, 30000); // Every 30 seconds

    // Handle visibility change - reconnect when tab becomes visible
    visibilityHandlerRef.current = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Tab became visible, checking socket connection...');

        if (!newSocket.connected) {
          console.log('🔄 Socket disconnected while tab was hidden, reconnecting...');
          newSocket.connect();
        } else {
          // Re-authenticate to ensure token is still valid
          const token = localStorage.getItem('token');
          if (token) {
            newSocket.emit('authenticate', { token });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', visibilityHandlerRef.current);

    // Handle online/offline events
    const handleOnline = () => {
      console.log('🌐 Network online, reconnecting socket...');
      if (!newSocket.connected) {
        newSocket.connect();
      }
    };

    const handleOffline = () => {
      console.log('📵 Network offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      if (newSocket) {
        console.log('🔌 Cleaning up socket (component unmount)');
        newSocket.disconnect();
      }

      // Clear intervals
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }

      // Remove event listeners
      if (visibilityHandlerRef.current) {
        document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user?._id]); // Only depend on user ID

  // Re-authenticate socket when AuthContext refreshes the token
  useEffect(() => {
    if (!authToken || !socketRef.current?.connected) return;
    console.log('🔐 AuthContext token refreshed, re-authenticating socket...');
    socketRef.current.emit('authenticate', { token: authToken });
  }, [authToken]);

  // Join workspace
  const joinWorkspace = useCallback((workspaceId) => {
    if (socket && isConnected && workspaceId) {
      socket.emit('workspace:join', { workspaceId });
      setCurrentWorkspace(workspaceId);
    }
  }, [socket, isConnected]);

  // Leave workspace
  const leaveWorkspace = useCallback(() => {
    setCurrentWorkspace(null);
    setWorkspaceUsers([]);
  }, []);

  // Update cursor position
  const updateCursor = useCallback((x, y, elementId = null) => {
    if (socket && isConnected && currentWorkspace) {
      socket.emit('workspace:cursor:update', { x, y, elementId });
    }
  }, [socket, isConnected, currentWorkspace]);

  // Start editing element
  const startEditingElement = useCallback((elementId) => {
    if (socket && isConnected && currentWorkspace) {
      socket.emit('workspace:element:editing:start', { elementId });
    }
  }, [socket, isConnected, currentWorkspace]);

  // Stop editing element
  const stopEditingElement = useCallback((elementId) => {
    if (socket && isConnected && currentWorkspace) {
      socket.emit('workspace:element:editing:stop', { elementId });
    }
  }, [socket, isConnected, currentWorkspace]);

  // Notify element created
  const notifyElementCreated = useCallback((element) => {
    if (socket && isConnected && currentWorkspace) {
      socket.emit('workspace:element:created', { element });
    }
  }, [socket, isConnected, currentWorkspace]);

  // Notify element updated
  const notifyElementUpdated = useCallback((element) => {
    if (socket && isConnected && currentWorkspace) {
      socket.emit('workspace:element:updated', { element });
    }
  }, [socket, isConnected, currentWorkspace]);

  // Notify element deleted
  const notifyElementDeleted = useCallback((elementId) => {
    if (socket && isConnected && currentWorkspace) {
      socket.emit('workspace:element:deleted', { elementId });
    }
  }, [socket, isConnected, currentWorkspace]);

  // Enter the review-queue presence room (reviewers only — the server checks).
  // The ref is written unconditionally so a call made before 'authenticated'
  // lands, or while the socket is down, still gets replayed on authentication.
  const enterReviewQueue = useCallback(() => {
    reviewQueueEnteredRef.current = true;
    if (socket && isConnected) {
      socket.emit('review:presence:enter', {});
    }
  }, [socket, isConnected]);

  // Leave the room and drop this socket's viewing entry server-side
  const exitReviewQueue = useCallback(() => {
    reviewQueueEnteredRef.current = false;
    reviewViewingTicketIdRef.current = null;
    // We leave the room before the server broadcasts, so the final state never
    // reaches us — clear locally rather than waiting for an event that won't come
    setReviewViewers({});
    if (socket && isConnected) {
      socket.emit('review:presence:exit', {});
    }
  }, [socket, isConnected]);

  // Idempotent state set, not a delta: this replaces whatever this socket was
  // viewing, and null means "viewing nothing" (back on the list, dialog closed)
  const setViewingReviewTicket = useCallback((ticketId) => {
    reviewViewingTicketIdRef.current = ticketId || null;
    if (socket && isConnected) {
      socket.emit('review:presence:viewing', { ticketId: ticketId || null });
    }
  }, [socket, isConnected]);

  // Force reconnect (can be called manually if needed)
  const forceReconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('🔄 Force reconnecting socket...');
      socketRef.current.disconnect();
      socketRef.current.connect();
    }
  }, []);

  const value = {
    socket,
    isConnected,
    currentWorkspace,
    workspaceUsers,
    joinWorkspace,
    leaveWorkspace,
    updateCursor,
    startEditingElement,
    stopEditingElement,
    notifyElementCreated,
    notifyElementUpdated,
    notifyElementDeleted,
    forceReconnect,
    reviewViewers,
    enterReviewQueue,
    exitReviewQueue,
    setViewingReviewTicket,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
